import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import {
  BondCloningExecuteRequest,
  BondCloningExecuteResponse,
  BondCloningImpact,
  BondCloningPreviewRequest,
  BondCloningPreviewResponse,
  BondCloningTableRow,
} from './bonds-cloning.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondsCloningService {
  private readonly endpoint = apiUrl('BondCloning');

  constructor(private readonly http: HttpClient) {}

  getSources(): Observable<BondCloningTableRow[]> {
    return this.http.get<unknown>(`${this.endpoint}/sources`).pipe(
      map((response) => this.mapRows(response)),
    );
  }

  getTargets(sourceTradeId: string): Observable<BondCloningTableRow[]> {
    return this.http.get<unknown>(`${this.endpoint}/targets`, {
      params: { sourceTradeId },
    }).pipe(
      map((response) => this.mapRows(response)),
    );
  }

  preview(payload: BondCloningPreviewRequest): Observable<BondCloningPreviewResponse> {
    return this.http.post<unknown>(`${this.endpoint}/preview`, payload).pipe(
      map((response) => this.normalizePreview(response)),
    );
  }

  execute(payload: BondCloningExecuteRequest): Observable<BondCloningExecuteResponse> {
    return this.http.post<unknown>(`${this.endpoint}/execute`, payload).pipe(
      map((response) => this.normalizeExecute(response)),
    );
  }

  private mapRows(response: unknown): BondCloningTableRow[] {
    return this.extractRows(response).map((item) => this.normalizeRow(item));
  }

  private extractRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = this.read(record, ['data', 'Data']);
      const items = this.read(record, ['items', 'Items']);
      if (Array.isArray(data)) {
        return data.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
      }
      if (Array.isArray(items)) {
        return items.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
      }
    }

    return [];
  }

  private normalizePreview(response: unknown): BondCloningPreviewResponse {
    const record = this.asRecord(response) ?? {};
    return {
      sourceTradeId: this.toText(this.read(record, ['sourceTradeId', 'SourceTradeId'])),
      targetTradeId: this.toText(this.read(record, ['targetTradeId', 'TargetTradeId'])),
      cloneType: this.toText(this.read(record, ['cloneType', 'CloneType'])),
      sourceTradeStatus: this.toText(this.read(record, ['sourceTradeStatus', 'SourceTradeStatus'])),
      sourceInstrumentType: this.toText(this.read(record, ['sourceInstrumentType', 'SourceInstrumentType'])),
      targetInstrumentType: this.toText(this.read(record, ['targetInstrumentType', 'TargetInstrumentType'])),
      source: this.toNullableRow(this.read(record, ['source', 'Source'])),
      target: this.toNullableRow(this.read(record, ['target', 'Target'])),
      impact: this.normalizeImpact(this.read(record, ['impact', 'Impact'])),
    };
  }

  private normalizeExecute(response: unknown): BondCloningExecuteResponse {
    const record = this.asRecord(response) ?? {};
    return {
      sourceTradeId: this.toText(this.read(record, ['sourceTradeId', 'SourceTradeId'])),
      targetTradeId: this.toText(this.read(record, ['targetTradeId', 'TargetTradeId'])),
      cloneType: this.toText(this.read(record, ['cloneType', 'CloneType'])),
      executedAtUtc: this.toText(this.read(record, ['executedAtUtc', 'ExecutedAtUtc'])),
      impact: this.normalizeImpact(this.read(record, ['impact', 'Impact'])),
    };
  }

  private toNullableRow(value: unknown): BondCloningTableRow | null {
    const record = this.asRecord(value);
    return record ? this.normalizeRow(record) : null;
  }

  private normalizeRow(record: Record<string, unknown>): BondCloningTableRow {
    return {
      dealId: this.toText(this.read(record, ['dealId', 'DealId'])),
      tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
      structureId: this.toText(this.read(record, ['structureId', 'StructureId'])),
      securityId: this.toText(this.read(record, ['securityId', 'SecurityId'])),
      securityType: this.toText(this.read(record, ['securityType', 'SecurityType'])),
      tradeDate: this.toText(this.read(record, ['tradeDate', 'TradeDate'])),
      settlementDate: this.toText(this.read(record, ['settlementDate', 'SettlementDate'])),
      maturityDate: this.toText(this.read(record, ['maturityDate', 'MaturityDate'])),
      currency: this.toText(this.read(record, ['currency', 'Currency'])),
      notional: this.toNullableNumber(this.read(record, ['notional', 'Notional'])),
      coupon: this.toNullableNumber(this.read(record, ['coupon', 'Coupon'])),
      us3mlCost: this.toNullableNumber(this.read(record, ['us3mlCost', 'Us3mlCost'])),
      tradeStatus: this.toText(this.read(record, ['tradeStatus', 'TradeStatus'])),
      matured: this.toBoolean(this.read(record, ['matured', 'Matured'])),
      marketType: this.toText(this.read(record, ['marketType', 'MarketType'])),
    };
  }

  private normalizeImpact(value: unknown): BondCloningImpact {
    const record = this.asRecord(value) ?? {};
    return {
      investorsToCopy: this.toNumber(this.read(record, ['investorsToCopy', 'InvestorsToCopy'])),
      tradeLeadsToCopy: this.toNumber(this.read(record, ['tradeLeadsToCopy', 'TradeLeadsToCopy'])),
      benchmarksToCopy: this.toNumber(this.read(record, ['benchmarksToCopy', 'BenchmarksToCopy'])),
      dealersToCopy: this.toNumber(this.read(record, ['dealersToCopy', 'DealersToCopy'])),
      notesToCopy: this.toNumber(this.read(record, ['notesToCopy', 'NotesToCopy'])),
      documentsToCopy: this.toNumber(this.read(record, ['documentsToCopy', 'DocumentsToCopy'])),
      targetInvestorsToReplace: this.toNumber(this.read(record, ['targetInvestorsToReplace', 'TargetInvestorsToReplace'])),
      targetTradeLeadsToReplace: this.toNumber(this.read(record, ['targetTradeLeadsToReplace', 'TargetTradeLeadsToReplace'])),
      targetBenchmarksToReplace: this.toNumber(this.read(record, ['targetBenchmarksToReplace', 'TargetBenchmarksToReplace'])),
      targetDealersToReplace: this.toNumber(this.read(record, ['targetDealersToReplace', 'TargetDealersToReplace'])),
      targetNotesToReplace: this.toNumber(this.read(record, ['targetNotesToReplace', 'TargetNotesToReplace'])),
      targetDocumentsToReplace: this.toNumber(this.read(record, ['targetDocumentsToReplace', 'TargetDocumentsToReplace'])),
      willTransferDocuments: this.toBoolean(this.read(record, ['willTransferDocuments', 'WillTransferDocuments'])),
      willDeleteSourceDocuments: this.toBoolean(this.read(record, ['willDeleteSourceDocuments', 'WillDeleteSourceDocuments'])),
    };
  }

  private read(record: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(record, key)) {
        return record[key];
      }
    }
    return undefined;
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toBoolean(value: unknown): boolean {
    return typeof value === 'boolean' ? value : false;
  }
}
